/**
 * API client.
 *
 * Two things here carry weight:
 *
 *  1. The access token is held **in memory only**. Putting it in localStorage
 *     would make it readable by any injected script, which is precisely what an
 *     XSS payload goes looking for. The refresh token lives in an HttpOnly
 *     cookie the browser sends automatically and JavaScript cannot read.
 *
 *  2. A 401 triggers a single refresh attempt and one replay of the original
 *     request. Concurrent 401s share one in-flight refresh, because firing
 *     several would rotate the token repeatedly and the server would correctly
 *     read the losers as token reuse — and revoke the whole family.
 */

import type { ProblemDetails } from '@lto/contracts';

const BASE_URL = import.meta.env['VITE_API_URL'] ?? 'https://lto-confirmation-api.jc-dagnalan.workers.dev';

let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function hasAccessToken(): boolean {
  return accessToken !== null;
}

/** A failed request, carrying the server's problem+json where there was one. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails | null;

  constructor(status: number, problem: ProblemDetails | null, fallback: string) {
    super(problem?.detail ?? fallback);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }

  /** Field-level messages, keyed by field, for form display. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const error of this.problem?.errors ?? []) out[error.field] = error.message;
    return out;
  }
}

interface RequestOptions {
  readonly method?: string;
  readonly body?: unknown;
  /** Skips the refresh-and-retry dance — used by the refresh call itself. */
  readonly noRetry?: boolean;
}

async function rawRequest(path: string, options: RequestOptions): Promise<Response> {
  return fetch(`${BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    // Required so the HttpOnly refresh cookie is sent.
    credentials: 'include',
    headers: {
      ...(accessToken === null ? {} : { authorization: `Bearer ${accessToken}` }),
      ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
    },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  });
}

/**
 * Exchanges the refresh cookie for a new access token.
 *
 * Deduplicated: every caller that arrives while a refresh is running awaits the
 * same promise rather than starting another.
 */
async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await rawRequest('/api/v1/auth/refresh', {
        method: 'POST',
        noRetry: true,
      });
      if (!response.ok) {
        accessToken = null;
        return false;
      }
      const body = (await response.json()) as { data: { accessToken: string } };
      accessToken = body.data.accessToken;
      return true;
    } catch {
      accessToken = null;
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response = await rawRequest(path, options);

  if (response.status === 401 && options.noRetry !== true) {
    const refreshed = await refreshSession();
    if (refreshed) response = await rawRequest(path, options);
  }

  return parseResponse<T>(response);
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let problem: ProblemDetails | null = null;
    try {
      problem = (await response.json()) as ProblemDetails;
    } catch {
      // Not every failure carries a JSON body — a gateway error, for instance.
    }
    throw new ApiError(response.status, problem, `Request failed (${response.status}).`);
  }

  if (response.status === 204) return undefined as T;

  const body = (await response.json()) as { data: T };
  return body.data;
}

/** SHA-256 of a file, hex. Storage refuses the upload if the bytes it receives differ (ATT-008). */
async function sha256Hex(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Uploads one file as the request body — not multipart — so the API can
 * stream it to storage, and refuse it on size or type before reading it.
 */
async function upload<T>(path: string, file: File): Promise<T> {
  const checksum = await sha256Hex(file);
  const send = () =>
    fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...(accessToken === null ? {} : { authorization: `Bearer ${accessToken}` }),
        'content-type': file.type === '' ? 'application/octet-stream' : file.type,
        'x-file-name': encodeURIComponent(file.name),
        'x-checksum-sha256': checksum,
      },
      body: file,
    });

  let response = await send();
  if (response.status === 401 && (await refreshSession())) response = await send();
  return parseResponse<T>(response);
}

/**
 * Downloads an export — RPT-006.
 *
 * A plain `<a href>` cannot be used: the access token lives in memory and is
 * sent as a header, which a browser-initiated navigation would not include.
 * So the file is fetched like any other request and handed to the browser as a
 * blob. The object URL is revoked immediately afterwards, because each one
 * pins the whole file in memory until the tab is closed.
 */
async function download(path: string, fallbackName: string): Promise<void> {
  let response = await rawRequest(path, {});
  if (response.status === 401) {
    if (await refreshSession()) response = await rawRequest(path, {});
  }

  if (!response.ok) {
    let problem: ProblemDetails | null = null;
    try {
      problem = (await response.json()) as ProblemDetails;
    } catch {
      /* a gateway error has no JSON body */
    }
    throw new ApiError(response.status, problem, `Download failed (${response.status}).`);
  }

  // Prefer the server's filename — the exact UTF-8 one when given, else its
  // ASCII fallback. It carries the report date, or an attachment's real name.
  const disposition = response.headers.get('content-disposition') ?? '';
  const exact = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  const plain = /filename="([^"]+)"/.exec(disposition);
  let filename = plain?.[1] ?? fallbackName;
  if (exact?.[1] !== undefined) {
    try {
      filename = decodeURIComponent(exact[1]);
    } catch {
      /* keep the fallback */
    }
  }

  const url = URL.createObjectURL(await response.blob());
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', ...(body === undefined ? {} : { body }) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', ...(body === undefined ? {} : { body }) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload,
  download,
};

/** Attempts to restore a session on page load, using the refresh cookie. */
export async function restoreSession(): Promise<boolean> {
  return refreshSession();
}
